import { useEffect, useRef, useState, useCallback } from 'react'
import { io } from 'socket.io-client'
import { fmtCoins } from '../../lib/cards'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'
import { Sounds, Music } from '../../lib/sounds'

interface Props {
  token: string; balance: number | null; displayName: string
  operatorId: string; playerId: string
}
type Phase = 'idle' | 'active' | 'hopping' | 'wobbling' | 'cooked' | 'cashed_out'
type Difficulty = 'easy' | 'medium' | 'hard' | 'hardcore'

const SURVIVAL    = { easy:0.91, medium:0.82, hard:0.70, hardcore:0.55 }
const DIFF_LABEL  = { easy:'Easy', medium:'Medium', hard:'Hard', hardcore:'Hardcore' }
const DIFF_CHANCE = { easy:'9%', medium:'18%', hard:'30%', hardcore:'45%' }
const DIFF_CSS    = { easy:'#22c55e', medium:'#3b82f6', hard:'#f59e0b', hardcore:'#ef4444' }
const CHIP_VALS   = [1, 5, 10, 50, 100, 500]
const CHIP_CSS    = ['#64748b','#22c55e','#3b82f6','#8b5cf6','#f59e0b','#ef4444']
const STONE_SPACING = 110
const HOP_DURATION  = 560
const HOP_HEIGHT    = 65
const WOBBLE_MS     = 650

function getMult(d: Difficulty, n: number) {
  return Math.floor(0.97 / Math.pow(SURVIVAL[d], n) * 100) / 100
}

const CSS = `
@keyframes ss-wobble {
  0%,100%{ transform:translateX(0) rotate(0deg) }
  15%    { transform:translateX(-7px) rotate(-4deg) }
  30%    { transform:translateX(7px) rotate(4deg) }
  45%    { transform:translateX(-5px) rotate(-2.5deg) }
  60%    { transform:translateX(5px) rotate(2.5deg) }
  75%    { transform:translateX(-3px) rotate(-1.5deg) }
  90%    { transform:translateX(3px) rotate(1.5deg) }
}
@keyframes ss-fall {
  0%  { transform:translateY(0) rotate(0deg); opacity:1 }
  60% { transform:translateY(90px) rotate(200deg); opacity:.7 }
  100%{ transform:translateY(140px) rotate(280deg); opacity:0 }
}
@keyframes sp-base {
  0%  { transform:scaleY(.2) scaleX(1.8); opacity:.6 }
  50% { transform:scaleY(1.4) scaleX(.8); opacity:.9 }
  100%{ transform:scaleY(1) scaleX(1); opacity:.85 }
}
@keyframes sp-up {
  0%  { opacity:1; transform:translate(0,0) scale(.3) }
  100%{ opacity:0; transform:translate(var(--tx),var(--ty)) scale(1) }
}
@keyframes wflow {
  0%  { transform:translateX(0) }
  100%{ transform:translateX(800px) }
}
@keyframes cloud-drift {
  0%  { transform:translateX(0) }
  100%{ transform:translateX(40px) }
}
@keyframes ss-prop {
  from { transform:rotate(0deg) }
  to   { transform:rotate(360deg) }
}
@keyframes ss-bubble-pop {
  0%  { transform:scale(0) rotate(-5deg); opacity:0 }
  70% { transform:scale(1.1) rotate(2deg); opacity:1 }
  100%{ transform:scale(1) rotate(0deg); opacity:1 }
}
@keyframes ss-bird-pickup {
  0%  { transform:translateY(45px) scale(.6); opacity:0 }
  60% { transform:translateY(-8px) scale(1.1); opacity:1 }
  100%{ transform:translateY(0) scale(1); opacity:1 }
}
@keyframes ss-plane-vib {
  0%,100%{ transform:translateY(0) }
  25%    { transform:translateY(-2px) }
  75%    { transform:translateY(2px) }
}
`
let cssInjected = false
function injectCSS() {
  if (cssInjected) return; cssInjected = true
  const s = document.createElement('style'); s.textContent = CSS
  document.head.appendChild(s)
}

// ── Bird SVGs ──────────────────────────────────────────────
function BirdIdle({ size = 58 }: { size?: number }) {
  const h = Math.round(size * 90 / 80)
  return (
    <svg viewBox="0 0 80 90" width={size} height={h} style={{ display:'block', filter:'drop-shadow(0 3px 6px rgba(0,0,0,0.35))' }}>
      <g transform="translate(6,40)"><path fill="#1a1a1a" d="M0 5 L-6 0 L-6 -5 L0 2 Z"/><path fill="#1a1a1a" d="M0 10 L-7 8 L-5 15 L2 12 Z"/></g>
      <circle fill="#DC2727" cx="40" cy="45" r="35"/>
      <ellipse fill="#B81E1E" cx="40" cy="70" rx="22" ry="8" opacity={0.6}/>
      <ellipse fill="#FDF5E6" cx="40" cy="58" rx="22" ry="18"/>
      <path fill="#DC2727" d="M28 12 Q32 5 38 9 Q42 4 48 10 Q45 14 40 13 Q35 15 28 12 Z"/>
      <ellipse fill="#fff" stroke="#000" strokeWidth={1.5} cx="33" cy="36" rx="9" ry="11"/>
      <ellipse fill="#fff" stroke="#000" strokeWidth={1.5} cx="48" cy="36" rx="9" ry="11"/>
      <circle fill="#000" cx="35" cy="38" r="3"/><circle fill="#000" cx="46" cy="38" r="3"/>
      <circle fill="#fff" cx="34" cy="37" r="1"/><circle fill="#fff" cx="45" cy="37" r="1"/>
      <path fill="#000" d="M23 28 L37 32 L36 26 L22 23 Z"/>
      <path fill="#000" d="M57 23 L44 26 L45 32 L58 28 Z"/>
      <path fill="#F7A91C" stroke="#D98E0F" strokeWidth={1.5} strokeLinejoin="round" d="M40 42 L58 48 L40 54 L33 48 Z"/>
      <line x1="40" y1="42" x2="40" y2="54" stroke="#D98E0F" strokeWidth={1}/>
      <ellipse fill="#fff" opacity={0.35} cx="32" cy="28" rx="8" ry="12" transform="rotate(-20 32 28)"/>
    </svg>
  )
}

function BirdJump({ size = 58 }: { size?: number }) {
  const h = Math.round(size * 90 / 80)
  return (
    <svg viewBox="0 0 80 90" width={size} height={h} style={{ display:'block', filter:'drop-shadow(0 3px 6px rgba(0,0,0,0.35))' }}>
      <defs>
        <radialGradient id="jbg2" cx="0.4" cy="0.3" r="0.7"><stop offset="0%" stopColor="#FF5A5A"/><stop offset="70%" stopColor="#E32D2D"/><stop offset="100%" stopColor="#B80000"/></radialGradient>
        <linearGradient id="jbk2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFD93D"/><stop offset="100%" stopColor="#E59400"/></linearGradient>
      </defs>
      <path d="M18 48 C12 48,8 54,10 60 C12 66,18 68,22 64 C20 58,20 52,18 48Z" fill="#E32D2D" stroke="#000" strokeWidth={0.8}/>
      <path d="M62 52 C68 52,72 58,70 64 C68 70,62 72,58 68 C60 62,60 56,62 52Z" fill="#E32D2D" stroke="#000" strokeWidth={0.8}/>
      <ellipse cx="40" cy="45" rx="28" ry="35" fill="url(#jbg2)" stroke="#000" strokeWidth={0.8}/>
      <ellipse cx="40" cy="65" rx="16" ry="12" fill="#FFF5E8"/>
      <path d="M32 10 C30 6,32 2,36 4 C38 8,36 12,32 10Z" fill="#E32D2D" stroke="#000" strokeWidth={0.6}/>
      <path d="M40 6 C40 2,44 0,46 4 C46 8,42 10,40 6Z" fill="#E32D2D" stroke="#000" strokeWidth={0.6}/>
      <path d="M48 10 C50 6,52 2,54 6 C54 10,50 12,48 10Z" fill="#E32D2D" stroke="#000" strokeWidth={0.6}/>
      <path d="M22 30 L38 34 L37 37 L21 34Z" fill="#1A1A1A"/>
      <path d="M58 30 L42 34 L43 37 L59 34Z" fill="#1A1A1A"/>
      <ellipse cx="32" cy="42" rx="8" ry="10" fill="#fff" stroke="#000" strokeWidth={0.8}/>
      <ellipse cx="48" cy="42" rx="8" ry="10" fill="#fff" stroke="#000" strokeWidth={0.8}/>
      <circle cx="33" cy="44" r="2.5" fill="#000"/><circle cx="49" cy="44" r="2.5" fill="#000"/>
      <circle cx="32" cy="43" r="0.8" fill="#fff"/><circle cx="48" cy="43" r="0.8" fill="#fff"/>
      <path d="M40 50 L58 53 L40 57Z" fill="url(#jbk2)" stroke="#000" strokeWidth={0.8}/>
      <path d="M40 57 L56 61 L40 62Z" fill="#E59400" stroke="#000" strokeWidth={0.8}/>
      <path d="M40 57 L56 57 L56 61 L40 62Z" fill="#5C0000"/>
      <path d="M12 45 C6 44,4 48,6 52 C8 54,12 52,13 48Z" fill="#1A1A1A"/>
      <path d="M13 50 C7 50,5 54,7 58 C9 60,13 58,14 54Z" fill="#1A1A1A"/>
    </svg>
  )
}

function BirdDead({ size = 58 }: { size?: number }) {
  const h = Math.round(size * 90 / 80)
  return (
    <svg viewBox="0 0 80 90" width={size} height={h} style={{ display:'block', filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>
      <defs>
        <radialGradient id="dbg2" cx="40%" cy="30%" r="70%"><stop offset="0%" stopColor="#FF6B5E"/><stop offset="100%" stopColor="#D32F2F"/></radialGradient>
        <linearGradient id="dbk2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FFC107"/><stop offset="100%" stopColor="#FF8F00"/></linearGradient>
      </defs>
      <g transform="rotate(6 40 45)">
        <path d="M8 42 L2 36 L6 40 L2 42 L8 44Z" fill="#1A1A1A"/>
        <path d="M9 46 L3 46 L7 44 L3 49 L9 48Z" fill="#1A1A1A"/>
        <ellipse cx="40" cy="45" rx="32" ry="30" fill="url(#dbg2)" stroke="#B71C1C" strokeWidth={1.5}/>
        <ellipse cx="40" cy="52" rx="20" ry="18" fill="#FF8A80" opacity={0.7}/>
        <path d="M22 32 Q27 38 32 36" fill="none" stroke="#8B0000" strokeWidth={3.5} strokeLinecap="round"/>
        <path d="M58 32 Q53 38 48 36" fill="none" stroke="#8B0000" strokeWidth={3.5} strokeLinecap="round"/>
        <g stroke="#1A1A1A" strokeWidth={2.5} strokeLinecap="round">
          <line x1="26" y1="38" x2="32" y2="44"/><line x1="32" y1="38" x2="26" y2="44"/>
          <line x1="48" y1="38" x2="54" y2="44"/><line x1="54" y1="38" x2="48" y2="44"/>
        </g>
        <path d="M40 45 L28 58 L40 62 L52 58Z" fill="url(#dbk2)" stroke="#E65100" strokeWidth={1}/>
        <path d="M35 17 Q32 12 36 14 L40 18Z" fill="#B71C1C"/>
        <path d="M40 16 Q40 10 45 14 L44 19Z" fill="#B71C1C"/>
        <path d="M45 17 Q48 12 44 14 L40 18Z" fill="#B71C1C"/>
        <g fill="#4FC3F7" opacity={0.85}>
          <ellipse cx="18" cy="28" rx="2" ry="3"/><ellipse cx="62" cy="32" rx="2.5" ry="3.5"/>
          <ellipse cx="56" cy="60" rx="2" ry="3"/><ellipse cx="40" cy="14" rx="1.8" ry="2.8"/>
        </g>
        <g stroke="#29B6F6" strokeWidth={1.2} strokeLinecap="round" fill="none" opacity={0.7}>
          <path d="M30 64 L29 67"/><path d="M48 66 L49 69"/><path d="M38 72 L38 75"/>
        </g>
      </g>
    </svg>
  )
}

// ── Stone & Platform ───────────────────────────────────────
function StoneSVG({ size = 96, active = false, passed = false }: { size?: number; active?: boolean; passed?: boolean }) {
  const h = Math.round(size * 55 / 110)
  return (
    <svg viewBox="0 0 110 55" width={size} height={h} style={{ display:'block' }}>
      <defs>
        <linearGradient id="st-top" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={passed ? '#3a5a3a' : active ? '#3a5a3a' : '#4a4a4a'}/>
          <stop offset="100%" stopColor={passed ? '#2a3a2a' : active ? '#2a4a2a' : '#353535'}/>
        </linearGradient>
        <linearGradient id="st-edge" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={passed ? '#2a3a2a' : '#2d2d2d'}/>
          <stop offset="100%" stopColor={passed ? '#1a2a1a' : '#1e1e1e'}/>
        </linearGradient>
      </defs>
      <ellipse cx="55" cy="32" rx="50" ry="20" fill="url(#st-edge)"/>
      <ellipse cx="55" cy="27" rx="50" ry="20" fill="url(#st-top)"/>
      {active && <ellipse cx="55" cy="27" rx="50" ry="20" fill="none" stroke="#22c55e" strokeWidth={2.5} opacity={0.8}/>}
    </svg>
  )
}

function PlatformSVG() {
  return (
    <svg viewBox="0 0 120 80" width={120} height={80} style={{ display:'block' }}>
      <defs>
        <linearGradient id="pl-grass" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7ED957"/><stop offset="100%" stopColor="#5CBD3B"/></linearGradient>
        <linearGradient id="pl-dirt" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#A67C52"/><stop offset="100%" stopColor="#7A5A3B"/></linearGradient>
        <linearGradient id="pl-stone" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#8D8D8D"/><stop offset="50%" stopColor="#B8B8B8"/><stop offset="100%" stopColor="#8D8D8D"/></linearGradient>
      </defs>
      <path d="M15 35 C15 30 18 28 23 28 L97 28 C102 28 105 30 105 35 L105 65 C105 70 102 73 97 73 L43 73 L40 68 L37 73 L23 73 C18 73 15 70 15 65 Z" fill="url(#pl-dirt)" stroke="#5A412B" strokeWidth={1.5} strokeLinejoin="round"/>
      <ellipse cx="30" cy="55" rx="6" ry="5" fill="url(#pl-stone)" stroke="#6B6B6B" strokeWidth={1}/>
      <ellipse cx="65" cy="58" rx="7" ry="5.5" fill="url(#pl-stone)" stroke="#6B6B6B" strokeWidth={1}/>
      <ellipse cx="85" cy="50" rx="5.5" ry="4.5" fill="url(#pl-stone)" stroke="#6B6B6B" strokeWidth={1}/>
      <rect x="15" y="22" width="90" height="13" rx="3" fill="url(#pl-grass)" stroke="#4A9E2F" strokeWidth={1.5}/>
      <path d="M22 22 Q24 18 26 22 M34 22 Q36 17 38 22 M46 22 Q48 18 50 22 M58 22 Q60 17 62 22 M70 22 Q72 18 74 22 M82 22 Q84 17 86 22 M94 22 Q96 18 98 22" fill="none" stroke="#4A9E2F" strokeWidth={1.8} strokeLinecap="round"/>
    </svg>
  )
}

// ── Cashout Animation SVGs ─────────────────────────────────
function PlaneSVG({ width = 140 }: { width?: number }) {
  const h = Math.round(width * 110 / 200)
  // Propeller center in scaled coords
  const propX = Math.round(width * 51 / 200) - 28
  const propY = Math.round(h * 58 / 110) - 28
  return (
    <div style={{ position:'relative', display:'inline-block', width, height:h }}>
      <svg viewBox="0 0 200 110" width={width} height={h} style={{ display:'block' }}>
        <defs>
          <linearGradient id="co-yb" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FFE15A"/><stop offset="100%" stopColor="#F4C430"/></linearGradient>
          <linearGradient id="co-rs" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF6B6B"/><stop offset="100%" stopColor="#D90429"/></linearGradient>
          <filter id="co-sh"><feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#000" floodOpacity="0.15"/></filter>
        </defs>
        <g filter="url(#co-sh)" stroke="#2C2C2C" strokeLinecap="round" strokeLinejoin="round">
          <path d="M150 44 C162 40 170 42 172 50 L172 60 C170 68 162 70 150 66 Z" fill="url(#co-yb)" strokeWidth={2.5}/>
          <path d="M158 46L168 35L172 45L162 48Z" fill="#EF4444" strokeWidth={2.5}/>
          <ellipse cx="110" cy="58" rx="55" ry="20" fill="url(#co-yb)" strokeWidth={2.5}/>
          <path d="M62 58 C80 56 140 56 158 57 L158 62 C140 61 80 61 62 63 Z" fill="url(#co-rs)" stroke="none"/>
          <path d="M95 62 C100 73 115 78 135 75 C132 70 118 68 102 66 C98 64 96 62 95 62 Z" fill="#FDE047" strokeWidth={2.5}/>
          <circle cx="120" cy="52" r="8" fill="#AEE6FF" strokeWidth={2.5}/>
          <ellipse cx="117" cy="49" rx="3" ry="1.5" fill="#fff" fillOpacity={0.8}/>
          <ellipse cx="59" cy="58" rx="8" ry="12" fill="#E5E7EB" strokeWidth={2.5}/>
          <g strokeWidth={3}>
            <line x1="95" y1="78" x2="95" y2="88"/>
            <line x1="130" y1="75" x2="132" y2="88"/>
          </g>
          <circle cx="95" cy="91" r="4.5" fill="#374151" strokeWidth={2}/>
          <circle cx="132" cy="91" r="4.5" fill="#374151" strokeWidth={2}/>
          <circle cx="95" cy="91" r="2" fill="#9CA3AF"/>
          <circle cx="132" cy="91" r="2" fill="#9CA3AF"/>
          <ellipse cx="110" cy="58" rx="55" ry="20" fill="none" strokeWidth={2.5}/>
        </g>
      </svg>
      {/* Spinning propeller overlay */}
      <div style={{ position:'absolute', left:propX, top:propY, width:56, height:56 }}>
        <svg viewBox="0 0 60 60" width={56} height={56}>
          <g style={{ transformOrigin:'30px 30px', animation:'ss-prop 0.4s linear infinite' }}>
            <path fill="#3a3a3a" stroke="#2a2a2a" strokeWidth={0.8} strokeLinejoin="round" d="M30 30 L32 18 Q30 5 28 18 Z"/>
            <path fill="#3a3a3a" stroke="#2a2a2a" strokeWidth={0.8} strokeLinejoin="round" d="M30 30 L40.4 35.2 Q51.9 40.5 40.4 42.9 Z"/>
            <path fill="#3a3a3a" stroke="#2a2a2a" strokeWidth={0.8} strokeLinejoin="round" d="M30 30 L19.6 35.2 Q8.1 40.5 19.6 42.9 Z"/>
            <circle fill="#4a4a4a" stroke="#2a2a2a" strokeWidth={0.8} cx="30" cy="30" r="5"/>
            <circle fill="#5a5a5a" cx="30" cy="30" r="2"/>
          </g>
        </svg>
      </div>
    </div>
  )
}

function RescueBubble() {
  return (
    <div style={{ animation:'ss-bubble-pop 0.45s cubic-bezier(.175,.885,.32,1.275) both', transformOrigin:'center bottom', filter:'drop-shadow(0 4px 12px rgba(0,0,0,0.4))' }}>
      <svg viewBox="0 0 160 80" width={200} height={100} style={{ display:'block' }}>
        <path fill="#fff" stroke="#1a1a1a" strokeWidth={4} strokeLinejoin="round"
          d="M20 12 C12 12,6 18,6 26 L6 40 C6 48,12 54,20 54 L58 54 L68 66 L78 54 L136 54 C144 54,150 48,150 40 L150 26 C150 18,144 12,136 12 Z"/>
        <text fontFamily="Arial Black,sans-serif" fontSize="20" fontWeight="900" fill="#1a1a1a" textAnchor="middle" x="78" y="38">RESCUED!</text>
        <text fontSize="18" textAnchor="middle" x="78" y="52">🎉</text>
      </svg>
    </div>
  )
}

// ── Water & Background ─────────────────────────────────────
function WaterBg({ waterH }: { waterH: number }) {
  return (
    <div style={{ position:'absolute', bottom:0, left:0, right:0, height:waterH, overflow:'hidden', pointerEvents:'none' }}>
      <svg viewBox="0 0 800 200" width="100%" height="100%" preserveAspectRatio="none" style={{ display:'block' }}>
        <defs><linearGradient id="wg2" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#2a9bc0"/><stop offset="100%" stopColor="#1a6a8a"/></linearGradient></defs>
        <rect width="800" height="200" fill="url(#wg2)"/>
        <g opacity="0.45">
          <path style={{ animation:'wflow 6s linear infinite' } as any} d="M-800,25 Q-750,15 -700,25 T-600,25 T-500,25 T-400,25 T-300,25 T-200,25 T-100,25 T0,25 T100,25 T200,25 T300,25 T400,25 T500,25 T600,25 T700,25 T800,25 T900,25 T1000,25 T1100,25 T1200,25 T1300,25 T1400,25 T1500,25 T1600,25" fill="none" stroke="#5bc8e8" strokeWidth="2"/>
          <path style={{ animation:'wflow 9s linear infinite', animationDelay:'-2s' } as any} d="M-800,70 Q-740,58 -680,70 T-560,70 T-440,70 T-320,70 T-200,70 T-80,70 T40,70 T160,70 T280,70 T400,70 T520,70 T640,70 T760,70 T880,70 T1000,70 T1120,70 T1240,70 T1360,70 T1480,70 T1600,70" fill="none" stroke="#5bc8e8" strokeWidth="2.5"/>
          <path style={{ animation:'wflow 11s linear infinite', animationDelay:'-5s' } as any} d="M-800,115 Q-720,100 -640,115 T-480,115 T-320,115 T-160,115 T0,115 T160,115 T320,115 T480,115 T640,115 T800,115 T960,115 T1120,115 T1280,115 T1440,115 T1600,115" fill="none" stroke="#5bc8e8" strokeWidth="3"/>
          <path style={{ animation:'wflow 7s linear infinite', animationDelay:'-1s' } as any} d="M-800,158 Q-760,148 -720,158 T-640,158 T-560,158 T-480,158 T-400,158 T-320,158 T-240,158 T-160,158 T-80,158 T0,158 T80,158 T160,158 T240,158 T320,158 T400,158 T480,158 T560,158 T640,158 T720,158 T800,158 T880,158 T960,158 T1040,158 T1120,158 T1200,158 T1280,158 T1360,158 T1440,158 T1520,158 T1600,158" fill="none" stroke="#5bc8e8" strokeWidth="2"/>
        </g>
      </svg>
    </div>
  )
}

function Splash({ worldX, bottom }: { worldX: number; bottom: number }) {
  return (
    <div style={{ position:'absolute', left:worldX-60, bottom:bottom-50, pointerEvents:'none', zIndex:10 }}>
      <svg viewBox="0 0 120 100" width={120} height={100} style={{ display:'block' }}>
        <defs>
          <radialGradient id="sp-d2" cx="40%" cy="25%" r="70%"><stop offset="0%" stopColor="#E8F8FF"/><stop offset="40%" stopColor="#A3E0FF"/><stop offset="100%" stopColor="#4AAFFF"/></radialGradient>
          <linearGradient id="sp-b2" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#4AAFFF" stopOpacity="0.9"/><stop offset="100%" stopColor="#2B8CE6" stopOpacity="0.8"/></linearGradient>
        </defs>
        <ellipse cx="60" cy="90" rx="18" ry="8" fill="url(#sp-b2)" style={{ animation:'sp-base .4s ease-out forwards', transformOrigin:'60px 85px' } as any}/>
        {([
          { rx:3, ry:4.5, tx:'-35px', ty:'-45px', d:'0s' },
          { rx:3.5, ry:5, tx:'-20px', ty:'-60px', d:'.05s' },
          { rx:3, ry:4.5, tx:'-8px', ty:'-68px', d:'.08s' },
          { rx:3, ry:4.5, tx:'8px', ty:'-68px', d:'.1s' },
          { rx:3.5, ry:5, tx:'20px', ty:'-60px', d:'.13s' },
          { rx:3, ry:4.5, tx:'35px', ty:'-45px', d:'.16s' },
          { rx:2, ry:3, tx:'0px', ty:'-72px', d:'.07s' },
        ] as any[]).map((dr, i) => (
          <ellipse key={i} cx="60" cy="85" rx={dr.rx} ry={dr.ry} fill="url(#sp-d2)"
            style={{ animation:`sp-up 1.2s cubic-bezier(.25,.46,.45,.94) ${dr.d} forwards`, transformOrigin:'60px 85px', opacity:0, '--tx':dr.tx,'--ty':dr.ty } as any}/>
        ))}
      </svg>
    </div>
  )
}

// ── Main Component ──────────────────────────────────────────
export function SteppingStone({ token, balance, displayName, operatorId, playerId }: Props) {
  injectCSS()

  const gameRef    = useRef<HTMLDivElement>(null)
  const socketRef  = useRef<any>(null)
  const hopAnimRef = useRef<number>()
  const coTimers   = useRef<ReturnType<typeof setTimeout>[]>([])

  const [hopY,         setHopY]         = useState(0)
  const [isHopping,    setIsHopping]    = useState(false)
  const [cameraX,      setCameraX]      = useState(0)
  const [birdWorldX,   setBirdWorldX]   = useState(0)
  const [phase,        setPhase]        = useState<Phase>('idle')
  const [diff,         setDiff]         = useState<Difficulty>('easy')
  const [stake,        setStake]        = useState(50)
  const [step,         setStep]         = useState(0)
  const [mults,        setMults]        = useState<number[]>([])
  const [localBal,     setLocalBal]     = useState<number | null>(balance)
  const [toast,        setToast]        = useState<string | null>(null)
  const [payout,       setPayout]       = useState(0)
  const [cashMult,     setCashMult]     = useState(0)
  const [showCooked,   setShowCooked]   = useState(false)
  const [showSplash,   setShowSplash]   = useState(false)
  const [splashX,      setSplashX]      = useState(0)
  const [wobbleIdx,    setWobbleIdx]    = useState(-1)
  // Cashout animation
  const [coPhase,      setCoPhase]      = useState(0)  // 0=none,1=enter,2=descend,3=pickup,4=rise,5=bubble,6=flyaway
  const [coLeft,       setCoLeft]       = useState(0)
  const [coTop,        setCoTop]        = useState(0)
  const [gameDims,     setGameDims]     = useState({ w:600, h:300 })

  const stakeRef = useRef(stake)
  const diffRef  = useRef(diff)
  const stepRef  = useRef(0)
  useEffect(() => { stakeRef.current = stake }, [stake])
  useEffect(() => { diffRef.current = diff }, [diff])
  useEffect(() => { stepRef.current = step }, [step])
  useEffect(() => { setLocalBal(balance) }, [balance])

  const BIRD_PCT  = 0.22
  const WATER_PCT = 0.44
  const STONE_W   = 96
  const STONE_H   = Math.round(STONE_W * 55 / 110)
  const BIRD_W    = 58
  const BIRD_H    = Math.round(BIRD_W * 90 / 80)
  const PLANE_W   = 140
  const PLANE_H   = Math.round(PLANE_W * 110 / 200)

  const STONE_BOTTOM = `calc(${WATER_PCT*100}% - ${STONE_H*0.45}px)`
  const BIRD_BOTTOM  = `calc(${WATER_PCT*100}% + ${STONE_H*0.18}px)`
  const PLAT_BOTTOM  = `calc(${WATER_PCT*100}% - 14px)`

  const showToast = (msg: string, dur = 3000) => {
    setToast(msg); setTimeout(() => setToast(null), dur)
  }

  function clearCoTimers() {
    coTimers.current.forEach(t => clearTimeout(t)); coTimers.current = []
  }

  // ── Socket ──────────────────────────────────────────────
  useEffect(() => {
    const GS = import.meta.env.VITE_GAME_SERVER_URL || 'http://localhost:3002'
    const s  = io(GS, { transports:['polling'], auth:{ token } })
    socketRef.current = s

    s.on('chicken:start_result', (data: any) => {
      if (data.success) {
        const gW = gameRef.current?.clientWidth || 600
        setMults(data.multipliers); setStep(0); setBirdWorldX(0)
        setCameraX(-(gW * BIRD_PCT)); setPhase('active')
        setIsHopping(false); setHopY(0)
        setLocalBal(prev => prev !== null ? prev - stakeRef.current : prev)
      } else { showToast(data.error || 'Failed to start'); setPhase('idle') }
    })

    s.on('chicken:hop_result', (data: any) => {
      if (!data.success) { showToast(data.error || 'Hop failed'); return }
      const gW = gameRef.current?.clientWidth || 600
      if (data.safe) {
        const ns = data.step; setStep(ns); setPhase('hopping')
        Sounds.hop()
        const fromW = stepRef.current * STONE_SPACING
        const toW   = ns * STONE_SPACING
        const birdScr = gW * BIRD_PCT
        setCameraX(toW - birdScr)
        setIsHopping(true); setBirdWorldX(toW)
        const start = performance.now()
        function frame(now: number) {
          const t = Math.min((now - start) / HOP_DURATION, 1)
          setHopY(-Math.sin(t * Math.PI) * HOP_HEIGHT)
          if (t < 1) hopAnimRef.current = requestAnimationFrame(frame)
          else { setHopY(0); setIsHopping(false); setPhase('active'); Sounds.land(); Sounds.tick() }
        }
        hopAnimRef.current = requestAnimationFrame(frame)
      } else {
        setWobbleIdx(stepRef.current); setPhase('wobbling')
        setTimeout(() => {
          setWobbleIdx(-1); setPhase('cooked'); Sounds.cooked(); Music.stop(); setShowCooked(false)
          setSplashX(stepRef.current * STONE_SPACING); setShowSplash(true)
          setTimeout(() => setShowSplash(false), 1500)
          showToast('💦 Splashed! Better luck next time!')
          setTimeout(() => setShowCooked(true), 2000)
        }, WOBBLE_MS)
      }
    })

    s.on('chicken:cashout_result', (data: any) => {
      if (data.success) {
        setPayout(data.payout); setCashMult(data.multiplier)
        setPhase('cashed_out')
        setLocalBal(prev => prev !== null ? prev + data.payout : prev)
      }
    })

    return () => { s.disconnect(); if (hopAnimRef.current) cancelAnimationFrame(hopAnimRef.current) }
  }, [token])

  // ── Cashout animation ──────────────────────────────────
  function startCashoutAnim() {
    clearCoTimers()
    Music.stop()
    const gW = gameRef.current?.clientWidth || 600
    const gH = gameRef.current?.clientHeight || 300
    setGameDims({ w:gW, h:gH })

    // Bird bottom in px from bottom of game container
    const birdBotPx = WATER_PCT * gH + STONE_H * 0.18
    // Bird center from top
    const birdCenterFromTop = gH - birdBotPx - BIRD_H / 2
    // Plane enters at top 8%
    const planeLandTop = Math.max(4, birdCenterFromTop - PLANE_H * 0.8)
    const planeInitTop = gH * 0.08

    // Horizontal: center plane on bird screen position
    const planeCenteredLeft = gW * BIRD_PCT - PLANE_W / 2

    setCoPhase(1)
    setCoLeft(gW + 10)   // start off-screen right
    setCoTop(planeInitTop)

    const t1 = setTimeout(() => {
      // Phase 1→2: plane moves to bird position horizontally
      setCoLeft(planeCenteredLeft)
    }, 50)

    const t2 = setTimeout(() => {
      // Phase 2: plane descends to bird level
      setCoPhase(2)
      setCoTop(planeLandTop)
      // Play sounds
      try { Sounds.stepRescuePlane?.() } catch {}
    }, 950)

    const t3 = setTimeout(() => {
      // Phase 3: bird picked up, plane rises
      setCoPhase(3)
      setCoTop(gH * 0.06)
      try { Sounds.stepRescueLift?.() } catch {}
    }, 1400)

    const t4 = setTimeout(() => {
      // Phase 4: bubble appears
      setCoPhase(4)
      try { Sounds.stepRescueFanfare?.() } catch {}
    }, 1900)

    const t5 = setTimeout(() => {
      // Phase 5: fly away
      setCoPhase(5)
      setCoLeft(-PLANE_W - 200)
    }, 2300)

    const t6 = setTimeout(() => {
      setCoPhase(0)
      clearCoTimers()
    }, 3200)

    coTimers.current = [t1, t2, t3, t4, t5, t6]
  }

  // ── Actions ──────────────────────────────────────────────
  const startGame = useCallback(() => {
    if (!socketRef.current || stake === 0) return
    Sounds.go()
    Music.start()
    setPhase('hopping')
    socketRef.current.emit('chicken:start', {
      player_id:playerId, operator_id:operatorId, difficulty:diffRef.current, stake,
    })
  }, [stake, playerId, operatorId])

  const hop = useCallback(() => {
    if (!socketRef.current || phase !== 'active') return
    socketRef.current.emit('chicken:hop', { player_id:playerId, operator_id:operatorId })
  }, [phase, playerId, operatorId])

  const cashout = useCallback(() => {
    if (!socketRef.current || phase !== 'active' || step === 0) return
    startCashoutAnim()
    socketRef.current.emit('chicken:cashout', { player_id:playerId, operator_id:operatorId })
  }, [phase, step, playerId, operatorId])

  const reset = () => {
    clearCoTimers(); setCoPhase(0)
    setPhase('idle'); setStep(0); setMults([]); setPayout(0)
    setIsHopping(false); setHopY(0); setShowCooked(false)
    setShowSplash(false); setWobbleIdx(-1)
    const gW = gameRef.current?.clientWidth || 600
    setBirdWorldX(0); setCameraX(-(gW * BIRD_PCT))
  }

  const currentMult = step > 0 ? getMult(diff, step) : 1
  const isCoAnim    = coPhase > 0

  // Cashout anim plane vertical transition
  const planeTransition = (() => {
    if (coPhase === 1) return `left 0.85s cubic-bezier(.2,.8,.3,1)`
    if (coPhase === 2) return `left 0s, top 0.45s ease-in`
    if (coPhase === 3) return `top 0.5s ease-out`
    if (coPhase === 5) return `left 0.8s cubic-bezier(.4,0,.6,1)`
    return 'none'
  })()

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:'#0f1a2e', color:'#e2e8f0', fontFamily:"'DM Sans',system-ui,sans-serif" }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 14px', background:'#0a1628', borderBottom:'1px solid rgba(255,255,255,0.07)', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:18 }}>🪨</span>
          <strong style={{ fontSize:13, letterSpacing:.5 }}>STEPPING STONE</strong>
          <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:4,
            background: phase==='active'||phase==='hopping' ? 'rgba(34,197,94,.2)' : phase==='cooked' ? 'rgba(239,68,68,.2)' : phase==='cashed_out' ? 'rgba(201,168,76,.2)' : 'rgba(34,197,94,.15)',
            color:      phase==='active'||phase==='hopping' ? '#22c55e' : phase==='cooked' ? '#ef4444' : phase==='cashed_out' ? '#c9a84c' : '#22c55e' }}>
            {phase==='idle'?'Ready':phase==='active'?'In Game':phase==='hopping'||phase==='wobbling'?'Hopping...':phase==='cooked'?'Splashed! 💦':'Won! 🏆'}
          </span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:11, color:'#475569' }}>Step {step}</span>
          <SoundToggleBtn />
          <div style={{ background:'rgba(201,168,76,.1)', border:'1px solid rgba(201,168,76,.25)', borderRadius:8, padding:'4px 10px', fontWeight:700, color:'#c9a84c', fontSize:13 }}>
            {localBal !== null ? `₹${fmtCoins(localBal)}` : '—'}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position:'absolute', top:50, left:'50%', transform:'translateX(-50%)', background:'rgba(42,155,192,.92)', borderRadius:8, padding:'6px 16px', fontSize:12, fontWeight:600, color:'white', zIndex:100, whiteSpace:'nowrap', pointerEvents:'none', boxShadow:'0 4px 16px rgba(0,0,0,.4)' }}>
          {toast}
        </div>
      )}

      {/* Game area */}
      <div ref={gameRef} style={{ flex:1, position:'relative', overflow:'hidden' }}>

        {/* Sky */}
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(180deg,#87CEEB 0%,#A8D8EA 55%,#C2E8F5 100%)' }}/>

        {/* Clouds */}
        <div style={{ position:'absolute', top:'6%', left:'12%', opacity:.75, animation:'cloud-drift 8s ease-in-out infinite alternate', pointerEvents:'none' }}>
          {[0,-14,14,-7,7].map((ox,i) => (
            <div key={i} style={{ position:'absolute', width:i===0?32:24, height:i===0?32:24, borderRadius:'50%', background:'white', left:40+ox, top:i<1?0:12 }}/>
          ))}
        </div>
        <div style={{ position:'absolute', top:'10%', left:'58%', opacity:.65, animation:'cloud-drift 11s ease-in-out infinite alternate-reverse', pointerEvents:'none' }}>
          {[0,-12,12,-6,6].map((ox,i) => (
            <div key={i} style={{ position:'absolute', width:i===0?28:20, height:i===0?28:20, borderRadius:'50%', background:'white', left:36+ox, top:i<1?0:10 }}/>
          ))}
        </div>

        {/* Water */}
        <WaterBg waterH={WATER_PCT * (gameRef.current?.clientHeight || 300)}/>
        <div style={{ position:'absolute', left:0, right:0, bottom:`${WATER_PCT*100}%`, height:4, background:'linear-gradient(90deg,rgba(91,200,232,0),rgba(91,200,232,.6),rgba(91,200,232,0))', pointerEvents:'none' }}/>

        {/* Scrolling world */}
        <div style={{
          position:'absolute', inset:0,
          transform:`translateX(${-cameraX}px)`,
          transition:isHopping ? `transform ${HOP_DURATION}ms cubic-bezier(.4,0,.2,1)` : 'none',
          willChange:'transform',
        }}>
          {/* Platform */}
          <div style={{ position:'absolute', left:-75, bottom:PLAT_BOTTOM, zIndex:2 }}>
            <PlatformSVG/>
          </div>

          {/* Arrow */}
          {mults.length > 0 && (
            <div style={{ position:'absolute', left:58, bottom:`calc(${WATER_PCT*100}% + 8px)`, fontSize:16, color:'rgba(255,255,255,0.4)', zIndex:2 }}>→</div>
          )}

          {/* Stones */}
          {mults.map((mult, i) => {
            const sn = i+1, worldX = sn * STONE_SPACING
            const isActive = sn === step, isPassed = sn < step
            const isWob = sn === wobbleIdx + 1, isNext = sn === step + 1
            return (
              <div key={i} style={{ position:'absolute', left:worldX-STONE_W/2, bottom:STONE_BOTTOM, zIndex:2,
                animation:isWob ? `ss-wobble ${WOBBLE_MS}ms ease-in-out` : 'none' }}>
                {isNext && phase==='active' && (
                  <div style={{ position:'absolute', inset:-6, borderRadius:'50%', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.3)', pointerEvents:'none' }}/>
                )}
                <StoneSVG size={STONE_W} active={isActive} passed={isPassed}/>
                <div style={{ position:'absolute', top:-38, left:'50%', transform:'translateX(-50%)',
                  background:isActive?'rgba(34,197,94,0.2)':isPassed?'rgba(34,197,94,0.12)':'rgba(99,102,241,0.2)',
                  border:`1px solid ${isActive?'rgba(34,197,94,0.5)':isPassed?'rgba(34,197,94,0.3)':'rgba(99,102,241,0.4)'}`,
                  borderRadius:12, padding:'2px 8px', whiteSpace:'nowrap',
                  fontSize:11, fontWeight:700, fontFamily:'monospace',
                  color:isActive?'#22c55e':isPassed?'#4ade80':'#a5b4fc' }}>
                  {mult.toFixed(2)}x
                </div>
                <div style={{ position:'absolute', bottom:-18, left:'50%', transform:'translateX(-50%)', fontSize:9, color:'rgba(255,255,255,0.25)', whiteSpace:'nowrap' }}>
                  #{sn}
                </div>
              </div>
            )
          })}

          {/* Splash */}
          {showSplash && <Splash worldX={splashX} bottom={parseFloat(BIRD_BOTTOM)}/>}

          {/* World bird — hidden during cashout anim */}
          <div style={{
            position:'absolute', left:birdWorldX-BIRD_W/2, bottom:BIRD_BOTTOM,
            transform:`translateY(${hopY}px)`, zIndex:5,
            opacity: isCoAnim && coPhase >= 2 ? 0 : 1,
            animation:phase==='cooked' ? 'ss-fall 1.8s ease-in forwards' : 'none',
            transition: 'opacity 0.15s',
          }}>
            {phase==='cooked' ? <BirdDead size={BIRD_W}/>
              : (isHopping || phase==='hopping' || phase==='wobbling') ? <BirdJump size={BIRD_W}/>
              : <BirdIdle size={BIRD_W}/>}
          </div>
        </div>

        {/* Current multiplier box */}
        {(phase==='active' || phase==='hopping') && step > 0 && (
          <div style={{ position:'absolute', top:10, right:10, background:'rgba(10,22,40,0.92)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:8, padding:'6px 14px', textAlign:'center', zIndex:20 }}>
            <div style={{ fontSize:9, color:'#64748b', letterSpacing:1, marginBottom:2 }}>CURRENT</div>
            <div style={{ fontSize:20, fontWeight:900, color:'#22c55e', fontFamily:'monospace' }}>{currentMult.toFixed(2)}x</div>
            <div style={{ fontSize:11, color:'#86efac' }}>₹{Math.floor(stake * currentMult)}</div>
          </div>
        )}

        {/* ── Cashout Animation Overlay ── */}
        {isCoAnim && (
          <div style={{ position:'absolute', inset:0, zIndex:40, pointerEvents:'none', overflow:'hidden' }}>

            {/* Plane container (moves as one unit) */}
            <div style={{
              position:'absolute',
              left: coLeft,
              top:  coTop,
              transition: planeTransition,
              willChange: 'left, top',
              animation: coPhase >= 1 && coPhase <= 4 ? 'ss-plane-vib 0.3s ease-in-out infinite' : 'none',
            }}>
              <PlaneSVG width={PLANE_W}/>

              {/* Bird riding on plane (phase 3+) */}
              {coPhase >= 3 && (
                <div style={{
                  position:'absolute',
                  left: PLANE_W * 0.38 - BIRD_W / 2,
                  top: -BIRD_H * 0.55,
                  animation:'ss-bird-pickup 0.35s ease-out forwards',
                }}>
                  <BirdJump size={BIRD_W}/>
                </div>
              )}
            </div>

            {/* Bird at stone during phase 1-2 (before pickup) */}
            {coPhase >= 1 && coPhase < 3 && (
              <div style={{
                position:'absolute',
                left: gameDims.w * BIRD_PCT - BIRD_W / 2,
                bottom: BIRD_BOTTOM,
                zIndex: 41,
              }}>
                <BirdIdle size={BIRD_W}/>
              </div>
            )}

            {/* RESCUED bubble */}
            {coPhase >= 4 && coPhase < 6 && (
              <div style={{
                position:'absolute',
                top:'5%',
                left:'50%',
                transform:'translateX(-50%)',
                zIndex:42,
              }}>
                <RescueBubble/>
              </div>
            )}
          </div>
        )}

        {/* Win overlay */}
        {phase==='cashed_out' && coPhase === 0 && (
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.8)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10, zIndex:50, backdropFilter:'blur(4px)' }}>
            <div style={{ fontSize:42, fontWeight:900, background:'linear-gradient(90deg,#c9a84c,#f0e68c,#c9a84c)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', letterSpacing:2 }}>YOU WIN!</div>
            <div style={{ fontSize:26, fontWeight:800, color:'#c9a84c' }}>+{fmtCoins(payout)} coins</div>
            <div style={{ fontSize:13, color:'#64748b' }}>at {cashMult.toFixed(2)}x</div>
            <button onClick={reset} style={{ marginTop:8, padding:'10px 28px', background:'#16a34a', border:'none', borderRadius:8, color:'white', fontSize:14, fontWeight:700, cursor:'pointer' }}>Play Again</button>
          </div>
        )}

        {/* Cooked overlay */}
        {showCooked && (
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.75)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, zIndex:30, backdropFilter:'blur(4px)' }}>
            <div style={{ fontSize:42, fontWeight:900, color:'#3b82f6', textShadow:'0 0 30px rgba(59,130,246,.8)', letterSpacing:2 }}>SPLASHED! 💦</div>
            <div style={{ fontSize:14, color:'#93c5fd' }}>Better luck next time!</div>
            <button onClick={reset} style={{ marginTop:8, padding:'10px 28px', background:'#1d4ed8', border:'none', borderRadius:8, color:'white', fontSize:14, fontWeight:700, cursor:'pointer' }}>Try Again</button>
          </div>
        )}
      </div>

      {/* Bottom panel */}
      <div style={{ flexShrink:0, background:'#0a1628', borderTop:'2px solid #1e293b' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 14px', borderBottom:'1px solid #1e293b', flexWrap:'wrap' }}>
          <span style={{ fontSize:11, color:'#475569', whiteSpace:'nowrap' }}>Difficulty</span>
          {(['easy','medium','hard','hardcore'] as Difficulty[]).map(d => (
            <button key={d} onClick={() => { if(phase==='idle') setDiff(d) }} disabled={phase!=='idle'}
              style={{ padding:'4px 11px', border:'none', borderRadius:6, fontSize:11, fontWeight:700, cursor:phase==='idle'?'pointer':'default', background:`${DIFF_CSS[d]}22`, color:DIFF_CSS[d], outline:diff===d?`2px solid ${DIFF_CSS[d]}`:'none', opacity:phase!=='idle'?.5:1, transition:'all .15s' }}>
              {DIFF_LABEL[d]}
            </button>
          ))}
          <span style={{ fontSize:11, color:'#475569', marginLeft:'auto', whiteSpace:'nowrap' }}>
            Risk: <strong style={{ color:DIFF_CSS[diff] }}>{DIFF_CHANCE[diff]}</strong>
          </span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 14px' }}>
          <div style={{ display:'flex', gap:4 }}>
            {CHIP_VALS.map((v,i) => (
              <button key={v} onClick={() => { if(phase==='idle') setStake(v) }} disabled={phase!=='idle'}
                style={{ width:30, height:30, borderRadius:'50%', background:`radial-gradient(circle at 35% 35%,${CHIP_CSS[i]}cc,${CHIP_CSS[i]}88)`, border:stake===v?'2px solid white':'2px solid rgba(255,255,255,.2)', color:'white', fontSize:9, fontWeight:700, cursor:phase==='idle'?'pointer':'default', opacity:phase==='idle'?1:.4, flexShrink:0 }}>
                {v>=1000?`${v/1000}k`:v}
              </button>
            ))}
          </div>
          <div style={{ padding:'5px 10px', background:'rgba(42,155,192,.12)', border:'1px solid rgba(42,155,192,.3)', borderRadius:6, color:'#5bc8e8', fontWeight:700, fontSize:13, minWidth:48, textAlign:'center' }}>
            ₹{stake}
          </div>
          {phase==='idle' && (
            <button onClick={startGame} style={{ flex:1, padding:10, background:'linear-gradient(135deg,#1d6fa4,#155a87)', border:'none', borderRadius:8, color:'white', fontSize:15, fontWeight:900, cursor:'pointer', boxShadow:'0 4px 16px rgba(29,111,164,.4)', letterSpacing:.5 }}>
              GO! 🪨
            </button>
          )}
          {(phase==='active' || phase==='hopping' || phase==='wobbling') && (<>
            <button onClick={cashout} disabled={step===0 || phase!=='active'}
              style={{ flex:1, padding:10, borderRadius:8, fontSize:13, fontWeight:700,
                background:step>0?'rgba(201,168,76,.15)':'rgba(255,255,255,.04)',
                border:`1px solid ${step>0?'rgba(201,168,76,.5)':'rgba(255,255,255,.1)'}`,
                color:step>0?'#c9a84c':'#334155',
                cursor:step>0&&phase==='active'?'pointer':'default',
                opacity:phase!=='active'?.5:1 }}>
              CASH OUT {step>0?`₹${Math.floor(stake*currentMult)}`:''}
            </button>
            <button onClick={hop} disabled={phase!=='active'}
              style={{ flex:1, padding:10, border:'none', borderRadius:8, color:'white', fontSize:15, fontWeight:900,
                background:phase!=='active'?'#1e293b':'linear-gradient(135deg,#1d6fa4,#155a87)',
                cursor:phase==='active'?'pointer':'default', opacity:phase!=='active'?.6:1,
                boxShadow:phase==='active'?'0 4px 16px rgba(29,111,164,.4)':'none' }}>
              {phase==='active' ? 'HOP →' : 'Hopping...'}
            </button>
          </>)}
          {(phase==='cashed_out' || phase==='cooked') && (
            <button onClick={reset} style={{ flex:1, padding:10, background:'#1e293b', border:'1px solid #334155', borderRadius:8, color:'#94a3b8', fontSize:13, fontWeight:700, cursor:'pointer' }}>
              New Game
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
